import React, { useEffect, useState } from 'react'
import { Modal, Space, Card, Row, Col, Button, Divider, Tag, theme, message } from 'antd'
import dayjs from 'dayjs'
import { goodXResponse } from '../../../../Bookings/utils/utils'
import { handleError } from '../../../../lib/utils'
import AgiliteSkeleton from '../../../../reusable-components/AgiliteSkeleton'

/**
 * Component for displaying claim response details in a modal
 */
const ClaimResponseModal = ({ isOpen, onClose, record }) => {
  const [showFinancials, setShowFinancials] = useState(false)
  const [claimResponse, setClaimResponse] = useState(null)
  const [loading, setLoading] = useState(true)
  const { token } = theme.useToken()

  const cardStyle = {
    marginBottom: 5,
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    borderRadius: 8
  }

  useEffect(() => {
    const getGoodXResponse = async () => {
      try {
        if (record.goodXStatus === 'Processed_successful') {
          setLoading(true)
          const response = await goodXResponse(record.goodXTicketNo)
          setClaimResponse(response)
        }
      } catch (e) {
        // Check if this is an IOD claim where detailed responses aren't available
        const isIODClaim = ['COID', 'FEM', 'SAPS', 'RMA'].includes(record.claimType)
        const isIODError =
          e.response?.data?.status === 'IOD_CLAIM' ||
          (e.response?.data?.rawResponse && e.response.data.rawResponse.includes('response is empty'))

        if (isIODClaim && isIODError) {
          console.log(`ℹ️ IOD claim (${record.claimType}) - detailed response not available (expected)`)
          setClaimResponse({
            message: `${record.claimType} claims do not provide detailed financial responses`,
            claimType: record.claimType,
            status: 'IOD_CLAIM_NO_DETAILS'
          })
        } else {
          handleError(e, true)
          message.error('Error fetching claim response. Please contact support.')
        }
      } finally {
        setLoading(false)
      }
    }

    if (record) getGoodXResponse()
    // eslint-disable-next-line
  }, [record])

  // Format amount to 2 decimal places safely
  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return '0.00'
    return parseFloat(amount).toFixed(2)
  }

  return (
    <Modal
      title={
        <div
          style={{
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            padding: '16px 24px',
            margin: '-20px -24px 20px'
          }}
        >
          <h3 style={{ margin: 0, color: token.colorTextHeading }}>Claim Response Details</h3>
        </div>
      }
      open={isOpen}
      onCancel={() => {
        onClose()
        setShowFinancials(false)
      }}
      footer={null}
      width={1000}
      destroyOnClose={true}
    >
      {loading ? (
        <AgiliteSkeleton spinnerTip='Loading Claim Response' skActive />
      ) : (
        <Space direction='vertical' style={{ width: '100%' }}>
          <Card
            type='inner'
            title='Claim & Patient Information'
            size='small'
            style={cardStyle}
            styles={{ header: { backgroundColor: token.colorPrimary, color: token.colorWhite } }}
          >
            <span style={{ fontWeight: 'bold', color: token.colorPrimary }}>Claim Information</span>
            <p>
              <strong>Claim Number:</strong>{' '}
              {
                claimResponse.ProviderClaimResponse.ProviderClaim[0].DispensingProviderClaim[0].CommonClaimHeaderInfo[0]
                  .ClaimNumber[0]
              }
            </p>
            <p>
              <strong>Transaction Date:</strong>{' '}
              {dayjs(
                claimResponse.ProviderClaimResponse.ProviderClaim[0].DispensingProviderClaim[0].CommonClaimHeaderInfo[0]
                  .DateTransactionCreated[0]
              ).format('YYYY-MM-DD')}
            </p>
            <p>
              <strong>Gross Amount:</strong> R
              {formatAmount(
                claimResponse.ProviderClaimResponse.ProviderClaim[0].DispensingProviderClaim[0].CommonClaimHeaderInfo[0]
                  .ClaimHeaderFinancial[0].CommonClaimBaseFinancialInfo[0].SubmittedGrossAmount[0]
              )}
            </p>
            <hr />
            <span style={{ fontWeight: 'bold', color: token.colorPrimary }}>Patient Information</span>
            <p>
              <strong>Name:</strong>{' '}
              {
                claimResponse.ProviderClaimResponse.ProviderClaim[0].DispensingProviderClaim[0].CommonClaimHeaderInfo[0]
                  .Policy[0].Patient[0].FirstName[0]
              }{' '}
              {
                claimResponse.ProviderClaimResponse.ProviderClaim[0].DispensingProviderClaim[0].CommonClaimHeaderInfo[0]
                  .Policy[0].Patient[0].CommonPersonInfo[0].Surname[0]
              }
            </p>
            <p>
              <strong>ID Number:</strong>{' '}
              {
                claimResponse.ProviderClaimResponse.ProviderClaim[0].DispensingProviderClaim[0].CommonClaimHeaderInfo[0]
                  .Policy[0].Patient[0].CommonPersonInfo[0].IdentityNumber[0]
              }
            </p>
            <p>
              <strong>Medical Aid:</strong>{' '}
              {
                claimResponse.ProviderClaimResponse.ProviderClaim[0].DispensingProviderClaim[0].CommonClaimHeaderInfo[0]
                  .Payor[0].Name[0]
              }
            </p>
            <p>
              <strong>Plan:</strong>{' '}
              {
                claimResponse.ProviderClaimResponse.ProviderClaim[0].DispensingProviderClaim[0].CommonClaimHeaderInfo[0]
                  .Payor[0].ClaimOption[0]
              }
            </p>
          </Card>

          <Card
            type='inner'
            title='Response Details'
            size='small'
            style={cardStyle}
            styles={{ header: { backgroundColor: token.colorSecondary, color: token.colorWhite } }}
          >
            <p>
              <strong>Primary Status:</strong>{' '}
              {claimResponse.ProviderClaimResponse.CommonProviderClaimResponseInfo[0].PrimaryTrackingStatus[0]}
            </p>
            <p>
              <strong>Claim Status:</strong>{' '}
              {renderClaimStatus(
                claimResponse.ProviderClaimResponse.CommonProviderClaimResponseInfo[0].ClaimHeaderStatusCode[0],
                token
              )}
            </p>
            <p>
              <strong>Status Code:</strong>{' '}
              {claimResponse.ProviderClaimResponse.CommonProviderClaimResponseInfo[0].ClaimHeaderStatusCode[0]}
            </p>
            <p>
              <strong>Transaction ID:</strong>{' '}
              {
                claimResponse.ProviderClaimResponse.CommonProviderClaimResponseInfo[0].ResponseNumber[0].Transaction[0]
                  .$.Id
              }
            </p>
            <Divider style={{ margin: '5px 0' }} />
            {claimResponse.ProviderClaimResponse.CommonProviderClaimResponseInfo[0].ProcessReasonCode?.map(
              (reason, index) => (
                <div key={index} style={cardStyle}>
                  <p>
                    <strong>Priority:</strong> {reason.Priority[0]}
                  </p>
                  <p>
                    <strong>Description:</strong> {reason.Description[0]}
                  </p>
                </div>
              )
            )}
          </Card>

          <Button type='primary' onClick={() => setShowFinancials(!showFinancials)} style={{ marginBottom: 10 }}>
            {showFinancials ? 'Hide Financial Details' : 'Show Financial Details'}
          </Button>

          {showFinancials && (
            <Card
              type='inner'
              title='Financial Details'
              size='small'
              style={cardStyle}
              styles={{ header: { backgroundColor: token.colorWarning, color: token.colorWhite } }}
            >
              {claimResponse.ProviderClaimResponse.ClaimFinancialOutcome?.[0]?.CommonFinancialOutcome?.[0] && (
                <>
                  <span style={{ fontWeight: 'bold', color: token.colorWarning }}>Overall Claim Amounts</span>
                  <Row gutter={[16, 8]} style={{ marginBottom: 10 }}>
                    <Col span={8}>
                      <p>
                        <strong>Submitted Amount:</strong> R
                        {formatAmount(
                          claimResponse.ProviderClaimResponse.ProviderClaim[0].DispensingProviderClaim[0]
                            .CommonClaimHeaderInfo[0].ClaimHeaderFinancial[0].CommonClaimBaseFinancialInfo[0]
                            .SubmittedGrossAmount[0]
                        )}
                      </p>
                    </Col>
                    <Col span={8}>
                      <p>
                        <strong>Approved Amount:</strong> R
                        {formatAmount(
                          claimResponse.ProviderClaimResponse.ClaimFinancialOutcome[0].CommonFinancialOutcome[0]
                            .ApprovedClaimAmount[0]
                        )}
                      </p>
                    </Col>
                    <Col span={8}>
                      <p>
                        <strong>Balance:</strong> R
                        {formatAmount(
                          claimResponse.ProviderClaimResponse.ProviderClaim[0].DispensingProviderClaim[0]
                            .CommonClaimHeaderInfo[0].ClaimHeaderFinancial[0].CommonClaimBaseFinancialInfo[0]
                            .SubmittedGrossAmount[0] -
                            claimResponse.ProviderClaimResponse.ClaimFinancialOutcome[0].CommonFinancialOutcome[0]
                              .ApprovedClaimAmount[0]
                        )}
                      </p>
                    </Col>
                  </Row>

                  <Divider style={{ margin: '10px 0' }} />
                  <span style={{ fontWeight: 'bold', color: token.colorWarning }}>Extended Financials</span>
                  <Row gutter={[16, 8]} style={{ marginBottom: 10 }}>
                    <Col span={8}>
                      <p>
                        <strong>Levy:</strong> R
                        {formatAmount(
                          claimResponse.ProviderClaimResponse.ClaimFinancialOutcome[0].ExtendedFinancials[0].Levy[0]
                        )}
                      </p>
                    </Col>
                    <Col span={8}>
                      <p>
                        <strong>Co-Payment:</strong> R
                        {formatAmount(
                          claimResponse.ProviderClaimResponse.ClaimFinancialOutcome[0].ExtendedFinancials[0]
                            .CoPayment[0]
                        )}
                      </p>
                    </Col>
                    <Col span={8}>
                      <p>
                        <strong>Overcharge:</strong> R
                        {formatAmount(
                          claimResponse.ProviderClaimResponse.ClaimFinancialOutcome[0].ExtendedFinancials[0]
                            .OverCharge[0]
                        )}
                      </p>
                    </Col>
                  </Row>

                  <Divider style={{ margin: '10px 0' }} />
                  <span style={{ fontWeight: 'bold', color: token.colorWarning }}>Line Items</span>
                  {claimResponse.ProviderClaimResponse.ClaimLineResponse.map((line, index) => {
                    const lineItem =
                      claimResponse.ProviderClaimResponse.ProviderClaim[0].DispensingProviderClaim[0].ClaimLineItem[
                        index
                      ]
                    const description =
                      lineItem.ClaimLineTariffItem?.[0]?.CommonClaimLineItemInfo?.[0]?.DescriptionOfItem?.[0] ||
                      lineItem.ClaimLineProductItem?.[0]?.CommonClaimLineItemInfo?.[0]?.DescriptionOfItem?.[0]
                    return (
                      <Card key={index} size='small' style={{ marginTop: 8 }}>
                        <p>
                          <strong>Description:</strong> {description}
                        </p>
                        <Row style={{ marginBottom: 10, marginTop: 10 }}>
                          <Col span={12}>
                            <p>
                              <strong>Submitted:</strong> R
                              {formatAmount(
                                lineItem.ClaimLineTariffItem?.[0]?.CommonClaimLineItemInfo?.[0]?.ClaimItemFinancial?.[0]
                                  ?.CommonClaimBaseFinancialInfo?.[0]?.SubmittedGrossAmount?.[0] ||
                                  lineItem.ClaimLineProductItem?.[0]?.CommonClaimLineItemInfo?.[0]
                                    ?.ClaimItemFinancial?.[0]?.CommonClaimBaseFinancialInfo?.[0]
                                    ?.SubmittedGrossAmount?.[0]
                              )}
                            </p>
                          </Col>
                          <Col span={12}>
                            <p>
                              <strong>Approved:</strong> R
                              {formatAmount(
                                line.LineItemFinancialOutcome[0].CommonFinancialOutcome[0].ApprovedClaimAmount[0]
                              )}
                            </p>
                          </Col>
                        </Row>
                        <p>
                          <strong>Status:</strong>{' '}
                          <Tag color={line.ProcessStatus[0] === 'Approved' ? token.colorSuccess : token.colorError}>
                            {line.ProcessStatus[0]}
                          </Tag>
                        </p>
                      </Card>
                    )
                  })}
                </>
              )}
            </Card>
          )}
        </Space>
      )}
    </Modal>
  )
}

/**
 * Helper function to render claim status with appropriate styling
 */
const renderClaimStatus = (statusCode, token) => {
  const statusMap = {
    mfREJ: { text: 'Medical Aid Rejected', color: token.colorError },
    mfAPP: { text: 'Medical Aid Approved', color: token.colorSuccess },
    swACC: { text: 'Switch Acknowledged', color: token.colorWarning },
    mfACK: { text: 'Medical Aid Acknowledged', color: token.colorWarning },
    swREJ: { text: 'Switch Rejected', color: token.colorError }
  }

  const status = statusMap[statusCode] || { text: statusCode, color: token.colorPrimary }
  return <span style={{ color: status.color }}>{status.text}</span>
}

export default ClaimResponseModal
